import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, CirclePlus, Clock, RefreshCw } from 'lucide-react';
import { getSelectedUnit } from '../../storage/Storage';
import { searchBestService, deleteBestService } from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import PageHeader from '../../PageHeader/PageHeader';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal';

function BestServices() {
    const navigate = useNavigate();

    const [bestServices, setBestServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchBestServices();
    }, []);

    const fetchBestServices = () => {
        setLoading(true);
        const json = {
            page: 1,
            limit: 100000,
            search: {
                unitIds: getSelectedUnit()?._id || getSelectedUnit()?.[0]
            }
        };

        HitApi(json, searchBestService).then((res) => {
            if (res?.data?.docs) {
                setBestServices(res.data.docs);
            } else if (res?.data && Array.isArray(res.data)) {
                setBestServices(res.data);
            } else {
                setBestServices([]);
            }
        }).catch((err) => {
            setBestServices([]);
            toast.error('Error fetching best services. Please try again.');
            console.error(err);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleEdit = (row) => {
        if (row?._id) navigate(`/bestservices/edit/${row._id}`);
        else toast.error('Unable to edit: Service ID not found');
    };

    const handleDelete = (row) => {
        setDeletingRow(row);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        setIsDeleting(true);
        const json = { _id: deletingRow?._id };

        HitApi(json, deleteBestService).then((res) => {
            if (res?.success || res?.statusCode === 200 || res?.message?.includes('deleted')) {
                toast.success('Best service deleted successfully!');
                fetchBestServices();
            } else {
                toast.error(res?.message || 'Failed to delete best service');
            }
        }).catch((err) => {
            toast.error('Error occurred while deleting. Please try again.');
            console.error(err);
        }).finally(() => {
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setDeletingRow(null);
        });
    };

    const getRandomColor = (index) => {
        const colors = [
            'border-l-blue-500', 'border-l-green-500', 'border-l-yellow-500',
            'border-l-red-500', 'border-l-purple-500', 'border-l-pink-500',
            'border-l-indigo-500', 'border-l-teal-500'
        ];
        return colors[index % colors.length];
    };

    const formatServiceTime = (serviceTime) => {
        if (!serviceTime) return '-';
        return `${serviceTime} min`;
    };

    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <div className="flex items-center justify-between mb-6">
                <PageHeader title={'Best Services'} description={'Manage your best/featured services'} />

                <button
                    onClick={() => navigate('/bestservices/add')}
                    className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                    <CirclePlus size={16} />
                    Add Best Service
                </button>
            </div>

            <div className="bg-white rounded-xl border">
                {/* Header */}
                <div className="p-4 border-b bg-gray-50 flex items-center justify-between rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            All Best Services ({bestServices.length})
                        </h2>
                    </div>
                    <button
                        onClick={fetchBestServices}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                        </div>
                    ) : bestServices.length > 0 ? (
                        <div className="space-y-3">
                            {bestServices.map((service, index) => (
                                <div
                                    key={service._id}
                                    className={`group flex items-center justify-between p-4 rounded-lg border-l-4 ${getRandomColor(index)} bg-gray-50 hover:bg-gray-100 transition-colors`}
                                >
                                    <div className="flex items-center gap-4">
                                        {service.img ? (
                                            <img
                                                src={service.img}
                                                alt={service.name}
                                                className="w-14 h-14 object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                                                <Star className="w-6 h-6 text-gray-400" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{service.name}</h3>
                                            {service.description && (
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-1">{service.description}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <div className="font-semibold text-gray-900">
                                                ₹{service.price?.toLocaleString() || '0'}
                                            </div>
                                            {service.member_price && (
                                                <div className="text-sm text-gray-500">
                                                    Member: ₹{service.member_price.toLocaleString()}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <Clock size={14} />
                                            <span>{formatServiceTime(service.service_time)}</span>
                                        </div>

                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <EditButton onEdit={() => handleEdit(service)} itemName="Best service" />
                                            <DeleteButton onDelete={() => handleDelete(service)} itemName="Best service" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Best Services Found</h3>
                            <p className="text-gray-500 mb-6">Add your first best service to get started.</p>
                            <button
                                onClick={() => navigate('/bestservices/add')}
                                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                <CirclePlus size={16} />
                                Add Best Service
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingRow(null);
                }}
                onConfirm={confirmDelete}
                itemName={deletingRow?.name}
                itemType="best service"
                isLoading={isDeleting}
                warningMessage="This will permanently delete this best service."
            />
        </div>
    );
}

export default BestServices;
